import { 
  ScenarioCategory, 
  StakeholderType, 
  ChallengeType, 
  Difficulty,
  LearningObjective 
} from '../types/simulation';

export interface StarterKit {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  targetAudience: string;
  scenarios: {
    title: string;
    stakeholderType: StakeholderType;
    difficulty: Difficulty;
    preview: string;
  }[];
  benefits: string[];
}

export const starterKits: StarterKit[] = [
  {
    id: 'new-pm-essentials',
    name: 'New PM Essentials',
    description: 'Master fundamental stakeholder conversations every new project manager encounters',
    icon: '🎯',
    color: 'blue',
    targetAudience: 'New PMs (0-3 years experience)',
    scenarios: [
      {
        title: 'Scope Change Request',
        stakeholderType: StakeholderType.CLIENT,
        difficulty: Difficulty.EASY,
        preview: 'The client wants to add new features mid-project. How do you handle this diplomatically?'
      },
      {
        title: 'Team Member Pushback',
        stakeholderType: StakeholderType.TEAM_MEMBER,
        difficulty: Difficulty.EASY,
        preview: 'A team member questions your timeline estimates in front of the group...'
      },
      {
        title: 'Executive Status Update',
        stakeholderType: StakeholderType.EXECUTIVE_SPONSOR,
        difficulty: Difficulty.MEDIUM,
        preview: 'Present project status to the executive sponsor who seems impatient...'
      },
      {
        title: 'Vendor Performance Issue',
        stakeholderType: StakeholderType.VENDOR,
        difficulty: Difficulty.MEDIUM,
        preview: 'Your vendor is consistently missing deadlines. Time for a difficult conversation...'
      }
    ],
    benefits: [
      'Build confidence in essential PM conversations',
      'Learn to handle scope creep professionally',
      'Master clear communication with executives',
      'Develop assertiveness while maintaining relationships'
    ]
  },
  {
    id: 'senior-pm-challenges',
    name: 'Senior PM Challenges',
    description: 'Advanced scenarios that separate good project managers from great leaders',
    icon: '🚀',
    color: 'purple',
    targetAudience: 'Experienced PMs (3-8 years) seeking advancement',
    scenarios: [
      {
        title: 'Cross-Functional Conflict Resolution',
        stakeholderType: StakeholderType.TEAM_MEMBER,
        difficulty: Difficulty.MEDIUM,
        preview: 'Marketing and Engineering teams are in conflict over requirements...'
      },
      {
        title: 'Budget Cut Negotiation',
        stakeholderType: StakeholderType.EXECUTIVE_SPONSOR,
        difficulty: Difficulty.HARD,
        preview: 'Your project budget has been cut by 30%. The sponsor expects the same deliverables...'
      },
      {
        title: 'Technical Debt vs Timeline Pressure',
        stakeholderType: StakeholderType.TECHNICAL_LEAD,
        difficulty: Difficulty.MEDIUM,
        preview: 'The technical lead insists on addressing tech debt, but launch date is fixed...'
      },
      {
        title: 'Stakeholder Alignment Crisis',
        stakeholderType: StakeholderType.CLIENT,
        difficulty: Difficulty.HARD,
        preview: 'Key stakeholders have conflicting visions for the project outcome...'
      }
    ],
    benefits: [
      'Navigate complex organizational politics',
      'Master resource negotiation and trade-off discussions',
      'Lead through ambiguity and competing priorities',
      'Develop senior-level influence and persuasion skills'
    ]
  },
  {
    id: 'pmo-leadership-track',
    name: 'PMO Leadership Track',
    description: 'Strategic conversations for PMs transitioning to portfolio and PMO leadership roles',
    icon: '🏢',
    color: 'green',
    targetAudience: 'Senior PMs moving into PMO/Portfolio management',
    scenarios: [
      {
        title: 'Portfolio Prioritization Meeting',
        stakeholderType: StakeholderType.EXECUTIVE_SPONSOR,
        difficulty: Difficulty.HARD,
        preview: 'Multiple executives want their projects prioritized. Resources are limited...'
      },
      {
        title: 'Resource Allocation Conflict',
        stakeholderType: StakeholderType.TEAM_MEMBER,
        difficulty: Difficulty.HARD,
        preview: 'Two critical projects need the same key resources. Both PMs are escalating...'
      },
      {
        title: 'Organizational Change Resistance',
        stakeholderType: StakeholderType.END_USER,
        difficulty: Difficulty.MEDIUM,
        preview: 'Department heads are resisting new PM methodologies you\'re implementing...'
      },
      {
        title: 'Board-Level Project Defense',
        stakeholderType: StakeholderType.EXECUTIVE_SPONSOR,
        difficulty: Difficulty.HARD,
        preview: 'The board is questioning a major project\'s ROI. You need to present the case...'
      }
    ],
    benefits: [
      'Develop strategic thinking and portfolio management skills',
      'Master C-level stakeholder communication',
      'Learn to drive organizational change and process improvement',
      'Build executive presence and board-level presentation skills'
    ]
  }
];